import { ref, computed, watch } from 'vue'
import tr from '../locales/tr.json'
import en from '../locales/en.json'

const STORAGE_KEY = 'fastpano_language'
const SUPPORTED_LANGUAGES = ['tr', 'en']
const DEFAULT_LANGUAGE = 'tr'

// Global language state
const currentLanguage = ref(DEFAULT_LANGUAGE)

// Language files
const translations = {
  tr,
  en
}

/**
 * Detect browser language
 * @returns {string} Language code (tr or en)
 */
function detectBrowserLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  
  const browserLang = navigator.language || navigator.userLanguage
  const langCode = browserLang.split('-')[0].toLowerCase()
  
  // Check if browser language is supported
  if (SUPPORTED_LANGUAGES.includes(langCode)) {
    return langCode
  }
  
  // Default to Turkish if browser language is not supported
  return DEFAULT_LANGUAGE
}

/**
 * Get saved language from localStorage or detect from browser
 * @returns {string} Language code
 */
function getInitialLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE
  
  // Check localStorage first
  const savedLang = localStorage.getItem(STORAGE_KEY)
  if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
    return savedLang
  }
  
  // Detect from browser
  return detectBrowserLanguage()
}

/**
 * Set language and save to localStorage
 * @param {string} lang - Language code
 */
function setLanguage(lang) {
  if (!SUPPORTED_LANGUAGES.includes(lang)) {
    console.warn(`Language ${lang} is not supported. Falling back to ${DEFAULT_LANGUAGE}`)
    lang = DEFAULT_LANGUAGE
  }
  
  currentLanguage.value = lang
  
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, lang)
    // Update HTML lang attribute
    document.documentElement.setAttribute('lang', lang)
  }
}

// Initialize language
if (typeof window !== 'undefined') {
  currentLanguage.value = getInitialLanguage()
  document.documentElement.setAttribute('lang', currentLanguage.value)
}

/**
 * i18n composable
 * @returns {object} i18n utilities
 */
export function useI18n() {
  // Get current translations
  const t = computed(() => {
    return translations[currentLanguage.value] || translations[DEFAULT_LANGUAGE]
  })
  
  // Get current language
  const locale = computed(() => currentLanguage.value)
  
  // Change language function
  const changeLanguage = (lang) => {
    setLanguage(lang)
  }
  
  // Get translation by key path (e.g., 'hero.subtitle')
  const translate = (keyPath) => {
    const keys = keyPath.split('.')
    let value = t.value
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key]
      } else {
        console.warn(`Translation key not found: ${keyPath}`)
        return keyPath
      }
    }
    
    return value
  }
  
  return {
    t,
    locale,
    changeLanguage,
    translate,
    supportedLanguages: SUPPORTED_LANGUAGES
  }
}

